export function bundleId(item) {
  const info = item.allocationId?.metadata?.bundleInfo;
  return (
    item.bundle_item_id ??
    (info?.isBundleComponent ? info.originalBundleId : null) ??
    null
  );
}

export function packingItemKey(item) {
  return JSON.stringify([item.item_id || item.productId?._id, bundleId(item)]);
}

export function packageRow(item) {
  return {
    item_id: item.productId._id,
    qty: 1,
    // Local provenance: historical null bundle fields alone do not prove standalone.
    bundleIdentityKnown: true,
    bundle_item_id: bundleId(item),
  };
}

export const PACKAGE_IDENTITY_ERROR =
  'Package item identity is ambiguous or incomplete. Review the package allocation assignment before generating a label.';

// Do not coerce, trim, or let a second identity hide malformed supplied bytes.
function hasMalformedParent(item) {
  return [
    item.bundle_item_id,
    item.allocationId?.metadata?.bundleInfo?.originalBundleId,
  ].some(
    parent =>
      parent != null &&
      (typeof parent !== 'string' ||
        parent.length !== 24 ||
        !/^[0-9a-fA-F]{24}$/.test(parent)),
  );
}

function hasPackingIdentity(row) {
  return row.bundle_item_id != null || row.bundleIdentityKnown === true;
}

function groupUnits(units) {
  const rows = new Map();
  for (const unit of units) {
    const key = packingItemKey(unit);
    if (rows.has(key)) rows.get(key).qty += 1;
    else rows.set(key, packageRow(unit));
  }
  return [...rows.values()];
}

// Resolve explicit identities first. An old untagged row may consume multiple
// identities only when it owns ALL remaining units of that item, with no sibling
// package competing for them. Array order is never evidence of bundle ownership.
export function assignPackageItems(packages, units, allowPartial = false) {
  const remaining = [...units];
  const result = packages.map(pack => ({ ...pack, items: [], packedItems: [] }));
  const pending = packages.flatMap((pack, index) => {
    const grouped = new Map();
    for (const row of pack.items.filter(row => row.qty > 0)) {
      const key = JSON.stringify([packingItemKey(row), hasPackingIdentity(row)]);
      if (grouped.has(key)) grouped.get(key).qty += Number(row.qty);
      else grouped.set(key, { ...row, qty: Number(row.qty) });
    }
    return [...grouped.values()].map(row => ({ row, index }));
  });
  pending.sort(
    (a, b) => Number(hasPackingIdentity(b.row)) - Number(hasPackingIdentity(a.row)),
  );
  // Order membership is checked at dispatch; null alone cannot distinguish a
  // direct order item from a component with missing parent metadata.
  let error = false;
  for (const { row, index } of pending) {
    const explicit = hasPackingIdentity(row);
    const candidates = remaining.filter(unit =>
      explicit
        ? packingItemKey(unit) === packingItemKey(row)
        : unit.productId._id === row.item_id,
    );
    const identities = new Set(candidates.map(packingItemKey));
    const competing = pending.some(
      other =>
        other.index !== index &&
        other.row.item_id === row.item_id &&
        !hasPackingIdentity(other.row),
    );
    if (
      (!allowPartial && candidates.length < row.qty) ||
      (!explicit && identities.size > 1 && (competing || candidates.length !== row.qty))
    ) {
      error = true;
      result[index].items.push({ ...row });
      continue;
    }
    const chosen = candidates.slice(0, row.qty);
    result[index].packedItems.push(...chosen);
    for (const unit of chosen) remaining.splice(remaining.indexOf(unit), 1);
  }
  for (const pack of result) {
    pack.items.push(...groupUnits(pack.packedItems));
    if (error) pack.identityError = PACKAGE_IDENTITY_ERROR;
  }
  return { packages: result, remaining };
}

export function initialPackingPackages(contents, labels = []) {
  // Unpacked siblings still carry identity evidence. Do not mistake the currently
  // packed subset for an authoritative historical package assignment.
  const assigned = assignPackageItems(
    labels.map(label => ({ label, items: label.items })),
    contents,
    true,
  );
  for (const pack of assigned.packages) {
    // Historical reconstruction must never erase a supplied malformed parent.
    if (pack.label.items.some(hasMalformedParent)) {
      pack.identityError = PACKAGE_IDENTITY_ERROR;
    }
    pack.packedItems = pack.packedItems.filter(unit => unit.status === 'packed');
    if (!pack.identityError) pack.items = groupUnits(pack.packedItems);
  }
  assigned.remaining = assigned.remaining.filter(unit => unit.status === 'packed');
  if (!labels.length || assigned.remaining.length) {
    assigned.packages.push({
      items: groupUnits(assigned.remaining),
      ...(assigned.packages.some(pack => pack.identityError) && {
        identityError: PACKAGE_IDENTITY_ERROR,
      }),
    });
  }
  return assigned.packages;
}

export function buildPackageItems(items, labelForPackage, labelAllocations, orderItems) {
  if ([...items, ...(labelAllocations || [])].some(hasMalformedParent)) {
    throw new Error(PACKAGE_IDENTITY_ERROR);
  }
  if (labelForPackage !== null && labelAllocations?.length) {
    const assigned = assignPackageItems([{ items }], labelAllocations);
    if (assigned.packages[0].identityError || assigned.remaining.length) {
      throw new Error(PACKAGE_IDENTITY_ERROR);
    }
    items = assigned.packages[0].items;
  }
  const grouped = new Map();
  for (const item of items) {
    const itemId = labelForPackage !== null ? item.item_id : item.productId._id;
    const qty = labelForPackage !== null ? item.qty : 1;
    // Label rows retain snake_case identity; loose contents are one unit per allocation.
    const bundleInfo = item.allocationId?.metadata?.bundleInfo;
    const bundleItemId =
      item.bundle_item_id ||
      (labelForPackage === null && bundleInfo?.isBundleComponent
        ? bundleInfo.originalBundleId
        : undefined);
    // Missing allocation metadata is not evidence of a standalone order line.
    // Never derive a parent from catalog membership or client-created provenance.
    if (
      orderItems !== undefined &&
      !bundleItemId &&
      !orderItems.some(row => row.item_id === itemId)
    ) {
      throw new Error(PACKAGE_IDENTITY_ERROR);
    }
    const key = JSON.stringify([itemId, bundleItemId || null]);
    const existing = grouped.get(key);
    if (existing) {
      existing.qty += qty;
    } else {
      grouped.set(key, {
        itemId,
        qty,
        ...(bundleItemId && { bundleItemId }),
      });
    }
  }
  return [...grouped.values()];
}
